export const isFiniteMetricValue = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const getFiniteMetricKeys = <T extends string>(
  points: Array<Partial<Record<T, unknown>>>,
  metrics: readonly T[],
): T[] => metrics.filter((metric) => points.some((point) => isFiniteMetricValue(point[metric])));

export const resolveDefaultSpeedMetric = <T extends string>(
  availableMetrics: readonly T[],
  preferredMetric: T = 'bsp' as T,
): T => availableMetrics.find((metric) => metric === preferredMetric) ?? availableMetrics[0] ?? preferredMetric;

const clamp01 = (value: number) => Math.min(Math.max(value, 0), 1);

const rgb = ([red, green, blue]: [number, number, number]) => `rgb(${red}, ${green}, ${blue})`;

const interpolateRgb = (
  start: [number, number, number],
  end: [number, number, number],
  factor: number,
): string => {
  const clampedFactor = clamp01(factor);
  const red = Math.round(start[0] + (end[0] - start[0]) * clampedFactor);
  const green = Math.round(start[1] + (end[1] - start[1]) * clampedFactor);
  const blue = Math.round(start[2] + (end[2] - start[2]) * clampedFactor);
  return rgb([red, green, blue]);
};

export const getMetricLineWidth = ({
  value,
  min,
  max,
  baseWidth = 2,
  widthRange = 10,
}: {
  value: unknown;
  min: number;
  max: number;
  baseWidth?: number;
  widthRange?: number;
}): number => {
  if (!isFiniteMetricValue(value) || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return baseWidth;
  }

  return baseWidth + widthRange * clamp01((value - min) / (max - min));
};

export const canMergeStyledLineFeatures = (
  currentProperties?: GeoJSON.GeoJsonProperties | null,
  nextProperties?: GeoJSON.GeoJsonProperties | null,
): boolean =>
  currentProperties?.color === nextProperties?.color &&
  currentProperties?.lineWidth === nextProperties?.lineWidth;

export const getTacticalMetricColor = ({
  value,
  min,
  max,
  colorMin = [200, 0, 0],
  colorMid = [200, 200, 200],
  colorMax = [0, 200, 0],
}: {
  value: number;
  min: number;
  max: number;
  colorMin?: [number, number, number];
  colorMid?: [number, number, number];
  colorMax?: [number, number, number];
}): string => {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return rgb(colorMid);
  }

  if (max <= 0) {
    return interpolateRgb(colorMin, colorMid, (value - min) / (max - min));
  }

  if (min >= 0) {
    return interpolateRgb(colorMid, colorMax, (value - min) / (max - min));
  }

  if (value <= 0) {
    return interpolateRgb(colorMin, colorMid, (value - min) / (0 - min));
  }

  return interpolateRgb(colorMid, colorMax, value / max);
};
